import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { AlertOctagon, Handshake, Crown, Sparkles, ArrowRight, ShieldAlert, Ban, DollarSign } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export const SubscriptionCancelModal = () => {
  const { user, profile, refreshProfile } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState<"payment_failed" | "user_requested">("user_requested");
  const [subId, setSubId] = useState<string | null>(null);

  useEffect(() => {
    if (!user) return;

    const checkCancellationNotice = async () => {
      try {
        // 1. Checar primeiro no perfil do usuário
        if (profile && (profile as any).cancel_seen === false && (profile as any).cancel_reason) {
          setReason((profile as any).cancel_reason);
          setOpen(true);
          return;
        }

        // 2. Checar na tabela de subscriptions se existe algum cancelamento não visualizado
        const { data: lastCanceled } = await supabase
          .from("subscriptions")
          .select("id, cancel_reason, cancel_seen")
          .eq("user_id", user.id)
          .eq("status", "canceled")
          .eq("cancel_seen", false)
          .order("created_at", { ascending: false })
          .limit(1)
          .maybeSingle();

        if (lastCanceled && (lastCanceled as any).cancel_seen === false) {
          setSubId(lastCanceled.id);
          setReason((lastCanceled as any).cancel_reason || "user_requested");
          setOpen(true);
        }
      } catch (err) {
        console.error("Erro ao verificar aviso de cancelamento:", err);
      }
    };

    checkCancellationNotice();
  }, [user, profile]);

  const handleClose = async () => {
    setOpen(false);
    if (!user) return;

    try {
      // Marcar como visto no perfil
      await supabase
        .from("profiles")
        .update({ cancel_seen: true } as any)
        .eq("user_id", user.id);

      // Marcar como visto na assinatura se houver ID
      if (subId) {
        await supabase
          .from("subscriptions")
          .update({ cancel_seen: true } as any)
          .eq("id", subId);
      }

      await refreshProfile();
    } catch (err) {
      console.error("Erro ao marcar cancel_seen:", err);
    }
  };

  const handleAction = async () => {
    await handleClose();
    navigate("/subscription");
  };

  if (!open) return null;

  const isPaymentFailed = reason === "payment_failed";

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4 overflow-hidden select-none">
        {/* Backdrop com escurecimento profundo */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black/90 backdrop-blur-2xl"
          onClick={handleClose}
        />

        {/* LUZES NEON LATERAIS EM VERMELHO PARA FALTA DE PAGAMENTO */}
        {isPaymentFailed && (
          <>
            <motion.div
              animate={{ opacity: [0.3, 0.9, 0.3] }}
              transition={{ repeat: Infinity, duration: 1.5, ease: "easeInOut" }}
              className="absolute top-0 bottom-0 left-0 w-8 md:w-16 bg-gradient-to-r from-red-600/60 to-transparent pointer-events-none z-10 shadow-[inset_30px_0_60px_rgba(220,38,38,0.8)]"
            />
            <motion.div
              animate={{ opacity: [0.3, 0.9, 0.3] }}
              transition={{ repeat: Infinity, duration: 1.5, ease: "easeInOut" }}
              className="absolute top-0 bottom-0 right-0 w-8 md:w-16 bg-gradient-to-l from-red-600/60 to-transparent pointer-events-none z-10 shadow-[inset_-30px_0_60px_rgba(220,38,38,0.8)]"
            />
          </>
        )}

        {/* CONTAINER DO MODAL CYBERPUNK / GAMER */}
        <motion.div
          initial={{ opacity: 0, scale: 0.85, y: 30 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          className={`relative z-20 w-full max-w-lg rounded-[32px] p-6 sm:p-8 border-2 text-white shadow-2xl overflow-hidden ${
            isPaymentFailed
              ? "bg-gradient-to-b from-[#1c0808] via-[#120404] to-black border-red-600/80 shadow-[0_0_80px_rgba(220,38,38,0.5)]"
              : "bg-gradient-to-b from-[#181325] via-[#0f0b1a] to-black border-amber-500/60 shadow-[0_0_80px_rgba(245,158,11,0.35)]"
          }`}
        >
          {/* Fundo dinâmico Gamer */}
          <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full blur-3xl opacity-30 pointer-events-none bg-red-600" />
          <div className="absolute -bottom-24 -left-24 w-64 h-64 rounded-full blur-3xl opacity-20 pointer-events-none bg-amber-500" />

          {/* CONTEÚDO PARA FALTA DE PAGAMENTO */}
          {isPaymentFailed ? (
            <div className="text-center space-y-5">
              {/* ÍCONE CENTRAL: CÉDULAS COM CÍRCULO DE PROIBIDO E BRILHO VERMELHO */}
              <div className="relative w-24 h-24 mx-auto flex items-center justify-center">
                <motion.div
                  animate={{ scale: [1, 1.08, 1], rotate: [0, 2, -2, 0] }}
                  transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
                  className="w-24 h-24 rounded-full bg-red-600/20 border-2 border-red-500 flex items-center justify-center shadow-[0_0_30px_rgba(220,38,38,0.6)] relative"
                >
                  <DollarSign className="w-12 h-12 text-red-500" />
                  <Ban className="w-20 h-20 text-red-600 absolute inset-0 m-auto stroke-[2.5]" />
                </motion.div>
              </div>

              <div>
                <span className="inline-block px-3 py-1 rounded-full bg-red-600/20 border border-red-500/40 text-red-400 text-[10px] font-black uppercase tracking-widest mb-2">
                  ⚠️ ALERTA DE SISTEMA • ACESSO SUSPENSO
                </span>
                <h2 className="text-2xl sm:text-3xl font-black uppercase tracking-tight text-white font-display">
                  Falta de Pagamento
                </h2>
                <p className="text-xs sm:text-sm text-red-200/80 mt-2 leading-relaxed font-medium">
                  Identificamos uma pendência financeira na sua assinatura do <strong className="text-white">Clube VIP</strong>. Seus descontos exclusivos em fotos e mídias foram temporariamente bloqueados.
                </p>
              </div>

              {/* Caixa Informativa Cyberpunk */}
              <div className="p-4 rounded-2xl bg-red-950/40 border border-red-800/40 text-left space-y-2">
                <div className="flex items-center gap-2 text-xs font-extrabold text-red-400">
                  <ShieldAlert className="w-4 h-4 text-red-500 shrink-0" />
                  <span>O que acontece agora?</span>
                </div>
                <ul className="text-[11px] text-zinc-300 space-y-1 list-disc pl-4">
                  <li>Suas fotos atribuídas passam a ser cobradas pelo valor normal de avulso.</li>
                  <li>Regularize seu plano para restaurar o valor VIP de R$ 3 ou R$ 6.</li>
                </ul>
              </div>

              {/* Botões de Ação */}
              <div className="flex flex-col gap-3 pt-2">
                <Button
                  onClick={handleAction}
                  className="w-full h-14 rounded-2xl font-black uppercase tracking-wider text-sm bg-gradient-to-r from-red-600 via-rose-600 to-orange-600 hover:from-red-500 hover:to-orange-500 text-white shadow-[0_0_25px_rgba(220,38,38,0.5)] transition-all flex items-center justify-center gap-2"
                >
                  <Sparkles className="w-5 h-5" />
                  REGULARIZAR E REATIVAR VIP AGORA
                </Button>
                <button
                  onClick={handleClose}
                  className="text-xs font-bold text-zinc-400 hover:text-white transition-colors py-1"
                >
                  Entendi, continuar sem o VIP por enquanto
                </button>
              </div>
            </div>
          ) : (
            /* CONTEÚDO PARA CANCELAMENTO A PEDIDO DO USUÁRIO */
            <div className="text-center space-y-5">
              {/* ÍCONE CENTRAL: APERTO DE MÃOS COM BRILHO CALOROSO */}
              <div className="relative w-24 h-24 mx-auto flex items-center justify-center">
                <motion.div
                  animate={{ scale: [1, 1.06, 1] }}
                  transition={{ repeat: Infinity, duration: 2.5, ease: "easeInOut" }}
                  className="w-24 h-24 rounded-full bg-amber-500/20 border-2 border-amber-400 flex items-center justify-center shadow-[0_0_35px_rgba(245,158,11,0.5)]"
                >
                  <Handshake className="w-12 h-12 text-amber-400" />
                </motion.div>
              </div>

              <div>
                <span className="inline-block px-3 py-1 rounded-full bg-amber-500/20 border border-amber-500/40 text-amber-300 text-[10px] font-black uppercase tracking-widest mb-2">
                  🤝 GRATIDÃO • NOSSO AGRADECIMENTO
                </span>
                <h2 className="text-2xl sm:text-3xl font-black uppercase tracking-tight text-white font-display">
                  Muito Obrigado!
                </h2>
                <p className="text-xs sm:text-sm text-amber-100/90 mt-2 leading-relaxed font-medium">
                  Agradecemos imensamente por todo o período em que você esteve conosco no <strong className="text-amber-400">Clube VIP</strong>! Sua presença fortalece nossa comunidade de atletas.
                </p>
              </div>

              {/* Mensagem Incentivadora */}
              <div className="p-4 rounded-2xl bg-amber-500/10 border border-amber-500/30 text-left space-y-2">
                <div className="flex items-center gap-2 text-xs font-extrabold text-amber-300">
                  <Crown className="w-4 h-4 text-amber-400 shrink-0" />
                  <span>A porta estará sempre aberta para você!</span>
                </div>
                <p className="text-[11px] text-zinc-300 leading-relaxed">
                  Sentiremos sua falta no topo da classificação! Quando desejar voltar a garantir suas fotos pelos menores preços da plataforma, basta reativar sua conta com um único clique.
                </p>
              </div>

              {/* Botões de Ação */}
              <div className="flex flex-col gap-3 pt-2">
                <Button
                  onClick={handleAction}
                  className="w-full h-14 rounded-2xl font-black uppercase tracking-wider text-sm bg-gradient-to-r from-amber-500 via-yellow-400 to-amber-500 hover:from-amber-400 hover:to-yellow-300 text-black shadow-[0_0_25px_rgba(245,158,11,0.4)] transition-all flex items-center justify-center gap-2"
                >
                  <Crown className="w-5 h-5 fill-black" />
                  VOLTAR A SER ASSINANTE VIP
                  <ArrowRight className="w-4 h-4" />
                </Button>
                <button
                  onClick={handleClose}
                  className="text-xs font-bold text-zinc-400 hover:text-white transition-colors py-1"
                >
                  Continuar para o aplicativo
                </button>
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </AnimatePresence>
  );
};
